import { useRef, useState, useEffect, useMemo } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import AppIcon from '@/components/content/app/AppIcon'
import Link from '@/components/ui/Link'
import { getBrandColors, cn, isPopulatedObject } from '@/lib/utils'
import { selectPrimaryRecommendation } from '@/lib/app-categories/discontinued'
import { easeShort } from '@/lib/transitions'
import type { App, AppCategoryRecommendation, Category, Media } from '@/payload-types'

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

const SPEED = 50
const APP_WIDTH_VERTICAL = 220
const APP_WIDTH_HORIZONTAL = 180
const OVERLAP = 112

type StackCarouselProps = {
  apps: App[]
  selectedApp: App | null
  setSelectedApp: (app: App | null) => void
  vertical?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export default function StackCarousel({
  apps,
  selectedApp,
  setSelectedApp,
  vertical = false,
  onMouseEnter,
  onMouseLeave,
}: StackCarouselProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [screenSize, setScreenSize] = useState({ width: 0, height: 0 })
  const [isVertical, setIsVertical] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [pendingApp, setPendingApp] = useState<App | null>(null)
  const debouncedApp = useDebounce(pendingApp, 50)

  useEffect(() => {
    setSelectedApp(debouncedApp)
  }, [debouncedApp, setSelectedApp])

  const primaryCategory = selectedApp?.categoryRecommendations?.docs
    ? (selectPrimaryRecommendation(
        selectedApp.categoryRecommendations.docs as AppCategoryRecommendation[],
      )?.category as Category | undefined)
    : undefined

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })

      if (vertical && window.innerWidth >= 1024) {
        setIsVertical(true)
      } else {
        setIsVertical(false)
      }

      if (!isReady) {
        setIsReady(true)
      }
    }

    handleResize()

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isReady, vertical])

  // Calculate dimensions and duration based on orientation
  const appWidth = isVertical ? APP_WIDTH_VERTICAL : APP_WIDTH_HORIZONTAL
  const singleSetSize = apps.length * (appWidth - OVERLAP)
  const duration = singleSetSize / SPEED

  // Calculate how many times we need to duplicate the apps to fill the screen plus buffer
  const minSetsNeeded = Math.max(
    2,
    Math.ceil((isVertical ? screenSize.height * 2 : screenSize.width * 2) / singleSetSize),
  )

  // Ensure minSetsNeeded is a valid array length
  const duplicatedApps = useMemo(() => {
    const validMinSetsNeeded =
      Number.isFinite(minSetsNeeded) && minSetsNeeded > 0 ? Math.min(minSetsNeeded, 100) : 2
    return Array(validMinSetsNeeded).fill(apps).flat()
  }, [apps, minSetsNeeded])

  return (
    <>
      <div className={cn('relative z-20', isVertical ? 'h-full' : 'mt-24 h-[260px] w-full')}>
        {isReady && (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={apps[0]?.id || 'empty'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={easeShort}
              ref={containerRef}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              className={cn(
                'hover:pause',
                isVertical
                  ? 'flex h-fit animate-marquee-vertical flex-col items-center'
                  : 'flex w-fit animate-marquee items-center',
              )}
              style={
                {
                  '--duration': `${duration}s`,
                  '--single-set-size': `${singleSetSize}px`,
                  '--offset-start': `-${OVERLAP * 2}px`,
                  '--offset-end': `${OVERLAP * 2}px`,
                } as React.CSSProperties
              }
            >
              {duplicatedApps.map((app: App, index: number) => {
                const appIcon = app?.icon as Media
                const appGlyph = app?.glyph as Media
                if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
                  return null
                }
                const { backgroundColor } = getBrandColors(app)
                return (
                  <Link
                    key={`${app.id}-${index}`}
                    href={`/apps/${app.slug}`}
                    onMouseEnter={() => setPendingApp(app)}
                    onMouseLeave={() => setPendingApp(null)}
                    style={{ zIndex: duplicatedApps.length - index }}
                    className={cn(
                      'group',
                      isVertical
                        ? '-mt-28 -rotate-[35deg] skew-x-[24deg] first:mt-0'
                        : '-ml-28 skew-y-[24deg] first:ml-0',
                    )}
                  >
                    <AppIcon
                      icon={appIcon}
                      glyph={appGlyph}
                      name={app.name}
                      size={appWidth}
                      invert={true}
                      background={backgroundColor}
                      className={cn(
                        'border border-primary-500 border-opacity-0 drop-shadow-marquee duration-500 group-hover:border-opacity-70',
                        isVertical
                          ? 'group-hover:my-28 group-hover:drop-shadow-marquee-active-vertical'
                          : 'group-hover:-translate-y-32 group-hover:drop-shadow-marquee-active',
                      )}
                    />
                  </Link>
                )
              })}
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      <AnimatePresence mode="wait">
        {selectedApp && (
          <motion.div
            key={selectedApp.id}
            initial={{
              opacity: 0,
              y: isVertical ? '-50%' : 10,
              x: isVertical ? 'calc(100% - 10px)' : '-50%',
            }}
            animate={{ opacity: 1, y: isVertical ? '-50%' : 0, x: isVertical ? '100%' : '-50%' }}
            exit={{
              opacity: 0,
              y: isVertical ? '-50%' : -10,
              x: isVertical ? 'calc(100% + 10px)' : '-50%',
            }}
            transition={easeShort}
            className={cn(
              'absolute z-30 hidden w-fit flex-col 2xl:flex',
              isVertical ? 'bottom-auto right-0 top-1/2' : 'bottom-8 left-1/2 items-center',
            )}
          >
            <span className="text-14 font-semibold text-white">{selectedApp.name}</span>
            {primaryCategory && (
              <span className="text-13 text-white/50">{primaryCategory.name}</span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
